import type { RouteObject } from 'react-router-dom';
import i18n from 'i18next';
import { LocationAssets } from 'pos-client/components/locations/assets/LocationAssets.tsx';
import { LocationHours } from 'pos-client/components/locations/hours/LocationHours.tsx';
import { LocationRoutes } from 'pos-client/components/locations/routes/LocationRoutes.tsx';
import { LocationServiceMatrix } from 'pos-client/components/locations/serviceAvailability/LocationServiceMatrix.tsx';
import { LocationSummary } from 'pos-client/components/locations/summary/LocationSummary.tsx';
import { LocationUsers } from 'pos-client/components/locations/users/LocationUsers.tsx';
import { PosPageContent } from 'pos-client/layouts/page/PosPageContent.tsx';
import { RouterError } from 'pos-client/pages/error/RouterError.tsx';
import { locationTabs } from 'pos-client/tabs/locationTabs.ts';
import type { BreadcrumbsRoute } from 'use-react-router-breadcrumbs';

import { AssetTypesPage } from './AssetTypes/AssetTypesPage.tsx';
import { ManageJobTypesPage } from './ManageJobTypes/ManageJobTypesPage.tsx';
import { LocationIntegrationsTab } from './Modals/LocationTabs/LocationIntegrationsTab.tsx';
import { DefaultSchedule } from './OpeningHours/HoursGroups/DefaultSchedule.tsx';
import { DropOffs } from './OpeningHours/HoursGroups/DropOffs.tsx';
import { hoursGroupsTabs } from './OpeningHours/HoursGroups/HoursGroupsTabs.ts';
import { NonTradingDates } from './OpeningHours/HoursGroups/NonTradingDates.tsx';
import { TradingTimes } from './OpeningHours/HoursGroups/TradingTimes.tsx';
import { OpeningHoursDetails } from './OpeningHours/OpeningHoursDetails.tsx';
import { OpeningHoursPage } from './OpeningHours/OpeningHoursPage.tsx';
import { LocationDetailsPage } from './LocationDetailsPage.tsx';
import { LocationsPage } from './LocationsPage.tsx';
const LocationsRoute: RouteObject[] = [
  {
    path: '/locations/',
    element: <LocationsPage />,
    errorElement: <RouterError />,
  },
  {
    path: '/opening-hours/',
    element: <OpeningHoursPage />,
    errorElement: <RouterError />,
  },
  {
    path: '/opening-hours/:id',
    element: <OpeningHoursDetails />,
    errorElement: <RouterError />,
    children: [
      {
        path: hoursGroupsTabs.defaultSchedule.key,
        breadcrumb: i18n.t('scheduling.defaultSchedule'),
        element: <DefaultSchedule />,
        errorElement: <RouterError hideToolbar />,
      },
      {
        path: hoursGroupsTabs.tradingTimes.key,
        breadcrumb: i18n.t('locations.trading.tradingTimes'),
        element: <TradingTimes />,
        errorElement: <RouterError hideToolbar />,
      },
      {
        path: hoursGroupsTabs.nonTradingDates.key,
        breadcrumb: i18n.t('locations.trading.nonTradingDates'),
        element: <NonTradingDates />,
        errorElement: <RouterError hideToolbar />,
      },
      {
        path: hoursGroupsTabs.dropOffs.key,
        breadcrumb: i18n.t('scheduling.slots.dropOffs'),
        element: <DropOffs />,
        errorElement: <RouterError hideToolbar />,
      },
    ] as BreadcrumbsRoute[],
  },
  {
    path: '/locations/:locationId',
    element: <LocationDetailsPage />,
    errorElement: <RouterError hideToolbar />,
    children: [
      {
        path: locationTabs.summary.key,
        element: <LocationSummary />,
        breadcrumb: i18n.t('locations.locationDetails'),
      },
      {
        path: locationTabs.hours.key,
        element: <LocationHours />,
        breadcrumb: i18n.t('locations.locationDetails'),
      },
      {
        path: locationTabs.assets.key,
        element: (
          <PosPageContent>
            <LocationAssets />
          </PosPageContent>
        ),
        breadcrumb: i18n.t('locations.locationDetails'),
      },
      {
        path: locationTabs.routes.key,
        element: <LocationRoutes />,
        breadcrumb: i18n.t('locations.locationDetails'),
      },
      {
        path: locationTabs.users.key,
        element: (
          <PosPageContent>
            <LocationUsers />
          </PosPageContent>
        ),
        breadcrumb: i18n.t('locations.locationDetails'),
      },
      {
        path: locationTabs.serviceMatrix.key,
        element: <LocationServiceMatrix />,
        breadcrumb: i18n.t('locations.locationDetails'),
      },
      {
        path: locationTabs.integrations.key,
        element: <LocationIntegrationsTab />,
        breadcrumb: i18n.t('locations.integrations.tabTitle'),
      },
    ] as BreadcrumbsRoute[],
  },
  {
    path: '/asset-types',
    element: <AssetTypesPage />,
    errorElement: <RouterError />,
  },
  {
    path: '/manage-job-types/',
    element: <ManageJobTypesPage />,
    errorElement: <RouterError />,
  },
];

export { LocationsRoute };
